import styled from 'styled-components';
import { Button } from 'components/elements';
import { colors, media, sizes, spacer } from '../../settings/style';

export const PageWrapper = styled.div`
  min-height: 100vh;
  color: ${colors.grey900};
`;

export const PageLayout = styled.div`
  max-width: ${sizes.xxl};
  margin: 0 auto;
  padding: 28px ${spacer.xl} 42px;
`;

export const BackButton = styled.button`
  border: 0;
  background: transparent;
  color: ${colors.grey900};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  cursor: pointer;
  margin-bottom: 18px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const HeroCard = styled.div<{ coverPhotoUrl?: string }>`
  min-height: 400px;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  background: ${({ coverPhotoUrl }) =>
    coverPhotoUrl
      ? `linear-gradient(180deg, rgba(10, 17, 35, 0.28) 0%, rgba(10, 17, 35, 0.75) 100%), url(${coverPhotoUrl})`
      : 'linear-gradient(180deg, #2f3b52 0%, #182230 100%)'};
  background-size: cover;
  background-position: center;
  padding: 20px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  box-shadow: 0 3px 8px rgba(16, 24, 40, 0.2);

  ${media.md`
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-end;
    gap: 12px;
  `}
`;

export const HeroInfo = styled.div`
  color: ${colors.white};
`;

export const HeroTitle = styled.h1`
  margin: 0;
  font-size: 42px;
  line-height: 1.1;
  font-weight: 500;
  font-family: 'Cormorant Garamond', Georgia, serif;
`;

export const HeroMeta = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.86);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;

  ${media.md`
    flex-wrap: nowrap;
    gap: 12px;
  `}
`;

export const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  ${media.md`
    width: 100%;
    justify-content: flex-start;
  `}
`;

export const HeroActionButton = styled(Button)`
  border: 0;
  border-radius: 10px;
  padding: 8px 14px;
  min-height: 34px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: ${colors.grey900};
  background: rgba(255, 255, 255, 0.95);

  &:hover,
  &:focus {
    background: #475467 !important;
    color: ${colors.white} !important;
  }
`;

export const MainGrid = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 18px;

  ${media.xl`
    grid-template-columns: 1fr;
  `}
`;

export const LeftColumn = styled.div`
  min-width: 0;
`;

export const RightColumn = styled.aside`
  min-width: 0;
`;

export const SurfaceCard = styled.section`
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 3px 8px rgba(16, 24, 40, 0.08);

  &:not(:first-child) {
    margin-top: 14px;
  }
`;

export const CardHeader = styled.section`
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 3px 8px rgba(16, 24, 40, 0.08);
  min-height: 150px;
  margin-top: 20px;
`;

export const EventsCard = styled.section`
  border: 1px solid #e4e7ec;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 3px 8px rgba(16, 24, 40, 0.08);
  margin-top: 30px;
  min-height: 500px;
`;

export const SectionTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 24px;
  line-height: 1.1;
  font-weight: 500;
  font-family: 'Cormorant Garamond', Georgia, serif;
  color: ${colors.grey900};
`;

export const AboutText = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #344054;
`;

export const EventTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  border-bottom: 1px solid #eaecf0;
  padding-top: 10px;
  margin-bottom: 10px;
`;

export const EventTab = styled.button<{ active?: boolean }>`
  border: 0;
  cursor: pointer;
  background: transparent;
  padding: 0 0 10px;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${({ active }) => (active ? '#8b6a2b' : '#475467')};
  border-bottom: 2px solid ${({ active }) => (active ? '#8b6a2b' : 'transparent')};
`;

export const EventGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  ${media.md`
    grid-template-columns: 1fr;
  `}
`;

export const SideTitle = styled.h4`
  margin: 0 0 12px;
  font-size: 20px;
  line-height: 1;
  font-family: 'Cormorant Garamond', Georgia, serif;
  color: ${colors.grey900};
`;

export const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid #eaecf0;

  &:first-child {
    border-top: 0;
    padding-top: 2px;
  }
`;

export const StatLabel = styled.span`
  font-size: 12px;
  color: #667085;
`;

export const StatValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.grey900};
`;
